'use client';

import { type ReactNode } from 'react';

import styles from './providerPageHeader.module.scss';

export type ProviderPageHeaderProps = {
  /** Page title (h1) */
  title: string;
  /** Optional subtitle or description text */
  subtitle?: string;
  /** Optional actions to render on the right (e.g., buttons) */
  actions?: ReactNode;
  /** Optional custom class for the header container */
  className?: string;
};

/**
 * ProviderPageHeader - Consistent header component for provider dashboard pages.
 *
 * Provides:
 * - h1 title
 * - Optional subtitle/description
 * - Optional right-aligned actions (buttons, etc.)
 * - Responsive layout (stacks on mobile)
 *
 * @example
 * ```tsx
 * <ProviderPageHeader
 *   title="Analytics"
 *   subtitle="View your performance metrics and trends."
 *   actions={<Button variant="secondary" onClick={refresh}>Refresh</Button>}
 * />
 * ```
 */
export function ProviderPageHeader({
  title,
  subtitle,
  actions,
  className
}: ProviderPageHeaderProps) {
  return (
    <header
      className={`${styles.providerPageHeader} ${className ?? ''}`.trim()}
    >
      <div className={styles.textContent}>
        <h1 className={styles.title}>{title}</h1>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </header>
  );
}
